import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchStoreBySlug } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import StoreLogo from "@/components/StoreLogo";
import WatchStoreButton from "@/components/WatchStoreButton";
import useSeo from "@/lib/useSeo";
import { ArrowLeft, ExternalLink } from "lucide-react";

export default function StoreDetail() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: false });

  useEffect(() => {
    let mounted = true;
    setState({ loading: true, data: null, err: false });
    fetchStoreBySlug(slug)
      .then((d) => mounted && setState({ loading: false, data: d, err: false }))
      .catch(() => mounted && setState({ loading: false, data: null, err: true }));
    return () => { mounted = false; };
  }, [slug]);

  const store = state.data?.store;
  const coupons = state.data?.coupons || [];
  const category = state.data?.category;
  const url = state.data?.store_url;

  useSeo({
    title: store ? `${store} coupons, promo codes & deals — The Couponer` : "Store · The Couponer",
    description: store
      ? `${coupons.length} verified ${store} coupons and deals. Save money with the latest ${store} promo codes, discounts, and cash-back offers. Updated daily.`
      : "Store landing page on The Couponer.",
    canonical: `https://thecouponer.com/store/${slug}`,
    keywords: store
      ? `${store} coupons, ${store} promo code, ${store} discount, ${store} deals, ${store} coupon code, ${store} sale, ${category || "shopping"} coupons`
      : undefined,
    jsonLd: store
      ? [
          {
            "@type": "Organization",
            "name": store,
            "url": url || `https://thecouponer.com/store/${slug}`,
            "sameAs": url ? [url] : undefined,
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thecouponer.com/" },
              { "@type": "ListItem", "position": 2, "name": "Stores", "item": "https://thecouponer.com/stores" },
              { "@type": "ListItem", "position": 3, "name": store, "item": `https://thecouponer.com/store/${slug}` },
            ],
          },
          {
            "@type": "ItemList",
            "name": `${store} coupons and deals`,
            "numberOfItems": coupons.length,
            "itemListElement": coupons.slice(0, 10).map((c, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Offer",
                "name": c.title,
                "url": `https://thecouponer.com/coupon/${c.slug || c.id}`,
                "seller": { "@type": "Organization", "name": store },
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
              },
            })),
          },
        ]
      : undefined,
  });

  if (state.loading) return <div className="mono text-xs p-10">LOADING...</div>;
  if (state.err || !store) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="store-not-found">
        <p className="serif text-4xl mb-3">Store not found.</p>
        <Link to="/stores" className="mono text-xs tracking-widest underline">BROWSE ALL STORES</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="store-detail-page">
      <Link
        to="/stores"
        className="text-[10px] tracking-[0.3em] text-[#5c5c5c] hover:text-[#ff6b6b] mb-4 inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> ALL STORES
      </Link>

      {/* Hero */}
      <header className="mb-10 flex flex-col md:flex-row md:items-end gap-6 md:gap-8">
        <div className="border-2 border-[#2d2a32] rounded-2xl p-4 bg-white hard-shadow-sm shrink-0">
          <StoreLogo store={store} storeUrl={url} size={72} />
        </div>
        <div className="flex-1 min-w-0">
          {category && (
            <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-2">
              {category.toUpperCase()}
            </div>
          )}
          <h1 className="serif text-5xl md:text-6xl leading-[0.95] tracking-tight" data-testid="store-heading">
            {store} <span className="italic text-[#ff6b6b]">coupons.</span>
          </h1>
          <p className="mt-3 text-[15px] text-[#3a3a38] leading-relaxed max-w-2xl">
            {coupons.length} verified {store} deal{coupons.length !== 1 ? "s" : ""} — promo codes, discounts, and cash-back offers. Curated and updated daily by The Couponer.
          </p>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener nofollow"
              data-testid="store-external-link"
              className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] font-bold text-[#2d2a32] hover:text-[#ff6b6b]"
            >
              VISIT {store.toUpperCase()} <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div className="mt-4">
            <WatchStoreButton store={store} />
          </div>
        </div>
      </header>

      {/* Deals grid */}
      {coupons.length === 0 ? (
        <div className="border-2 border-dashed border-[#2d2a32]/40 p-10 text-center bg-white rounded-xl">
          <p className="text-sm text-[#5c5c5c]">No active deals right now. Check back tomorrow.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-coupons-grid">
          {coupons.map((c) => (
            <CouponCard key={c.id} coupon={c} />
          ))}
        </div>
      )}

      {/* SEO body — help Google understand the page */}
      <section className="mt-16 pt-8 border-t-2 border-dashed border-[#2d2a32] prose max-w-3xl text-[#3a3a38]">
        <h2 className="serif text-3xl mb-3">How to use {store} coupons</h2>
        <p className="text-[15px] leading-relaxed mb-4">
          Pick a deal above, click <strong>COPY CODE</strong> to grab the promo code
          (or <strong>GET DEAL</strong> for auto-applied discounts), then head to
          {url ? <> <a href={url} target="_blank" rel="noopener nofollow" className="underline">{store}</a></> : ` ${store}`}
          . Paste the code at checkout to see your discount reflected in the total.
        </p>
        <p className="text-[14px] text-[#5c5c5c] leading-relaxed">
          Every {store} deal on this page is verified and updated by The Couponer&#39;s community and editors. If a code stops working, you can flag it — we&#39;ll pull it from rotation.
        </p>
      </section>
    </div>
  );
}
